import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      window.open(`https://kaspreadsheet.com/search.html?keyword=${encodeURIComponent(searchValue)}`, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section className="relative min-h-[100dvh] flex flex-col items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Premium%20fashion%20ecommerce%20product%20flat%20lay%20collection%20aesthetic%20minimalist%20photography%20luxury%20streetwear%20items%20scattered%20on%20soft%20neutral%20background%20warm%20natural%20lighting%20editorial%20style%20high%20end%20clothing%20shoes%20accessories%20composition&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
          alt="Fashion products collection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 pt-24 pb-16 flex flex-col items-center text-center">
        <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium tracking-wide uppercase mb-6">
          allchinabuyspread.de
        </span>

        <h1 className="max-w-4xl text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-5">
          {t('hero_title')}
        </h1>

        <p className="max-w-2xl text-base md:text-lg text-white/80 leading-relaxed mb-8">
          {t('hero_subtitle')}
        </p>

        {/* Search Bar + Dropdown */}
        <div className="w-full max-w-2xl mb-8">
          <div className="relative flex items-center bg-white/95 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden">
            {/* Category Dropdown */}
            <div className="relative shrink-0">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-3.5 text-sm font-medium text-foreground-700 bg-white border-r border-background-200/80 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="hidden sm:inline">{t('nav_categories')}</span>
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-background-200/80 max-h-72 overflow-y-auto z-50 py-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
                    >
                      <span className="w-2 h-2 rounded-full bg-primary-400 shrink-0"></span>
                      {t(cat.nameKey)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={handleSearch} className="flex-1 flex items-center">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('search_placeholder')}
                className="w-full px-4 py-3.5 text-sm bg-transparent text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              <button
                type="submit"
                className="px-4 md:px-6 py-3.5 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors shrink-0 cursor-pointer"
              >
                <i className="ri-search-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            </form>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl cursor-pointer"
          >
            {t('cta_allchinabuy_spreadsheet')}
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-white text-foreground-900 text-sm font-semibold hover:bg-white/90 transition-all shadow-lg hover:shadow-xl cursor-pointer"
          >
            {t('cta_buy_it_now')}
            <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}